import React, { useState, useMemo } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  IconButton, 
  Paper, 
  TextField, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Breadcrumbs,
  Link,
  Snackbar,
  Alert,
  Grid,
  Switch,
  FormControlLabel
} from '@mui/material';
import { 
  Home as HomeIcon, 
  Edit, 
  Delete, 
  Search, 
  ChevronRight,
  Close as CloseIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Loader from './Loader';
import { db } from './Dexie';
import { useLiveQuery } from 'dexie-react-hooks';

function TelegramUsers() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  // Ottieni gli utenti da Dexie
  const users = useLiveQuery(() => db.users.toArray());

  // Filtraggio utenti
  const filteredUsers = useMemo(() => {
    const data = users || [];
    return data.filter(u => {
      const username = u.username || '';
      const email = u.email || '';
      return username.toLowerCase().includes(searchQuery.toLowerCase()) || 
             email.toLowerCase().includes(searchQuery.toLowerCase());
    });
  }, [users, searchQuery]);

  if (!users) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Loader />
        <Typography sx={{ mt: 2 }}>Caricamento utenti...</Typography>
      </Box>
    );
  }

  const handleOpenModal = (user = null) => {
    setSelectedUser(user || { 
      user: '', 
      username: '', 
      first_name: '', 
      last_name: '', 
      is_bot: 0, 
      language_code: '', 
      date: '', 
      last_update: '', 
      next_update: '', 
      auth: 0 
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
  };

  const handleSaveUser = async () => {
    try {
      const userData = {
        ...selectedUser,
        is_bot: selectedUser.is_bot ? 1 : 0,
        auth: parseInt(selectedUser.auth) || 0
      };

      if (userData.id) {
        await db.users.put(userData);
        setToast({ open: true, message: 'Utente aggiornato con successo', severity: 'success' });
      } else {
        await db.users.add(userData);
        setToast({ open: true, message: 'Nuovo utente creato con successo', severity: 'success' });
      }
      handleCloseModal();
    } catch (error) {
      console.error(error);
      setToast({ open: true, message: 'Errore durante il salvataggio', severity: 'error' });
    }
  };

  const handleDeleteUser = async (id) => {
    if (window.confirm('Sei sicuro di voler eliminare questo utente?')) {
      await db.users.delete(id);
      setToast({ open: true, message: 'Utente eliminato', severity: 'info' });
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Breadcrumbs */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Breadcrumbs separator={<ChevronRight fontSize="small" />} sx={{ color: 'rgba(255,255,255,0.7)' }}>
          <Link
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: 'inherit' }}
            onClick={() => navigate('/')}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Typography color="primary.main" sx={{ fontWeight: 600 }}>Gestione Utenti Telegram</Typography>
        </Breadcrumbs>
      </Box>

      {/* Search Header */}
      <Paper sx={{ p: 3, mb: 4, backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Cerca per nome utente..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: 'rgba(255,255,255,0.5)' }} />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Users Table */}
      <TableContainer component={Paper} sx={{ backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: 2 }}>
        <Table>
          <TableHead sx={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Username</TableCell>
              <TableCell>Data</TableCell>
              <TableCell align="center">Bot</TableCell>
              <TableCell align="center">Auth</TableCell>
              <TableCell align="right">Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, opacity: 0.5 }}>
                  Nessun utente trovato.
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((user) => (
                <TableRow 
                  key={user.id} 
                  hover 
                  onClick={() => handleOpenModal(user)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Typography variant="body2">{user.user || 'N/A'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 32, height: 32, fontSize: '0.875rem' }}>
                        {(user.username || user.first_name || 'U').charAt(0).toUpperCase()}
                      </Avatar>
                      <Typography variant="subtitle2">{user.username || 'N/A'}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell variant="caption">
                    {user.date || '--/--/----'}
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={(user.is_bot === 1 || user.is_bot === true) ? 'Sì' : 'No'} 
                      size="small" 
                      color={(user.is_bot === 1 || user.is_bot === true) ? 'warning' : 'default'} 
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 700, 
                        color: (user.auth > 0) ? 'primary.main' : 'rgba(255,255,255,0.5)' 
                      }}
                    >
                      {user.auth ?? 0}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton 
                      size="small" 
                      color="primary" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenModal(user);
                      }}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      color="error" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteUser(user.id);
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modal Modifica */}
      <Dialog 
        fullScreen 
        open={isModalOpen} 
        onClose={handleCloseModal}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          backgroundColor: 'rgba(255,255,255,0.05)',
          px: 4,
          py: 2
        }}>
          <Typography variant="h6">Dettagli Utente Telegram: {selectedUser?.username || selectedUser?.first_name}</Typography>
          <IconButton onClick={handleCloseModal} color="inherit">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Grid container spacing={3} sx={{ pt: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="User"
                fullWidth
                value={selectedUser?.user || ''}
                onChange={(e) => setSelectedUser({ ...selectedUser, user: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Username"
                fullWidth
                value={selectedUser?.username || ''}
                onChange={(e) => setSelectedUser({ ...selectedUser, username: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First Name"
                fullWidth
                value={selectedUser?.first_name || ''}
                onChange={(e) => setSelectedUser({ ...selectedUser, first_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last Name"
                fullWidth
                value={selectedUser?.last_name || ''}
                onChange={(e) => setSelectedUser({ ...selectedUser, last_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Language Code"
                fullWidth
                inputProps={{ maxLength: 2 }}
                value={selectedUser?.language_code || ''}
                onChange={(e) => setSelectedUser({ ...selectedUser, language_code: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Auth"
                fullWidth
                type="number"
                value={selectedUser?.auth || 0}
                onChange={(e) => setSelectedUser({ ...selectedUser, auth: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Date"
                fullWidth
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={selectedUser?.date || ''}
                onChange={(e) => setSelectedUser({ ...selectedUser, date: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Last Update"
                fullWidth
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={selectedUser?.last_update || ''}
                onChange={(e) => setSelectedUser({ ...selectedUser, last_update: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Next Update"
                fullWidth
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={selectedUser?.next_update || ''}
                onChange={(e) => setSelectedUser({ ...selectedUser, next_update: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(selectedUser?.is_bot === 1 || selectedUser?.is_bot === true)}
                    onChange={(e) => setSelectedUser({ ...selectedUser, is_bot: e.target.checked ? 1 : 0 })}
                    color="primary"
                  />
                }
                label="Is Bot"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseModal}>Annulla</Button>
          <Button variant="contained" onClick={handleSaveUser}>Salva</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default TelegramUsers;
